import { useMemo } from 'react';
import { useLang } from '../i18n.jsx';

/**
 * Paginador numerado de la casa.
 *
 * Primera y ultima pagina siempre a la vista, ventana alrededor de la actual y
 * puntos suspensivos en los huecos; con 7 o menos salen todas. Lo usan la
 * tabla del meta y la rejilla de cartas: UN componente para que los dos se
 * comporten y se vean identicos, blancos de dedo incluidos.
 *
 * LAS FLECHAS NO SE MUEVEN NUNCA, y esa es la regla que manda sobre el resto.
 * Avanzar rapido es clic-clic-clic sin levantar el dedo del sitio, y para eso
 * el blanco tiene que estar quieto. Antes no lo estaba: el numero de huecos
 * cambiaba con la pagina -- en la 1 salian cuatro (1 2 3 … 10) y en la 5 salian
 * siete (1 … 4 5 6 … 10) --, asi que la fila se ensanchaba, las flechas se
 * corrian bajo el cursor y el tercer clic caia sobre un numero. Ir a la pagina
 * 6 cuando querias la 8.
 *
 * De ahi las dos decisiones de aqui abajo:
 *
 *   1. SIEMPRE SIETE HUECOS cuando hay mas de siete paginas. Nunca cuatro ni
 *      cinco: los que sobran los ocupan los puntos suspensivos.
 *   2. Todos los huecos MIDEN LO MISMO, incluidos los puntos, y ese ancho sale
 *      de cuantos digitos tiene la ultima pagina (--pag-digitos) para que
 *      pasar de la 9 a la 10 tampoco mueva nada.
 *
 * Con siete paginas o menos salen todas y el ancho ya era constante de por si.
 */
export default function Paginador({ pagina, total, onPagina, className = '' }) {
  const { t } = useLang();

  const numeros = useMemo(() => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    // Los cinco huecos de en medio, entre la primera y la ultima. Siempre cinco.
    let medio;
    if (pagina <= 4) medio = [2, 3, 4, 5, '…'];
    else if (pagina >= total - 3) medio = ['…', total - 4, total - 3, total - 2, total - 1];
    else medio = ['…', pagina - 1, pagina, pagina + 1, '…'];
    return [1, ...medio, total];
  }, [pagina, total]);

  if (total <= 1) return null;

  return (
    <nav
      className={`paginador ${className}`.trim()}
      aria-label={t('pgLabel')}
      style={{ '--pag-digitos': String(total).length }}
    >
      <button
        type="button"
        className="paginador__btn paginador__flecha"
        disabled={pagina <= 1}
        onClick={() => onPagina(pagina - 1)}
        aria-label={t('pgPrev')}
      >
        ‹
      </button>
      {numeros.map((n, i) =>
        n === '…' ? (
          <span key={`gap-${i}`} className="paginador__puntos" aria-hidden="true">…</span>
        ) : (
          <button
            key={n}
            type="button"
            className={`paginador__btn${n === pagina ? ' is-active' : ''}`}
            aria-current={n === pagina ? 'page' : undefined}
            onClick={() => onPagina(n)}
          >
            {n}
          </button>
        )
      )}
      <button
        type="button"
        className="paginador__btn paginador__flecha"
        disabled={pagina >= total}
        onClick={() => onPagina(pagina + 1)}
        aria-label={t('pgNext')}
      >
        ›
      </button>
    </nav>
  );
}
